import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { PitchArrivalTypeSettingsEditor } from "@/components/pitches/PitchArrivalTypeSettingsEditor";
import { api } from "@/lib/apiClient";
import { isDevDataDeletionHost } from "@/lib/platform";
import {
  PITCH_USAGE_TYPE_OPTIONS,
  arrivalSettingsFromApi,
  arrivalSettingsToApi,
  emptyArrivalTypeSettings,
  hasArrivalTypeRestrictions,
  type PitchArrivalTypeSettingsForm,
  type PitchUsageType,
} from "@/lib/pitchUsageType";
import { PitchBlockPeriodsEditor } from "@/components/pitches/PitchBlockPeriodsEditor";
import { PitchFeatureSelect } from "@/components/pitches/PitchFeatureSelect";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import type { ArrivalTypeRecord, Pitch, PitchCategory } from "@/types/camping";

export interface EditPitchModalProps {
  open: boolean;
  pitch: Pitch | null;
  categories: PitchCategory[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
  canEdit: boolean;
}

interface PitchFormState {
  code: string;
  label: string;
  category_id: string;
  size_sqm: string;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
  is_blocked: boolean;
  is_active: boolean;
  notes: string;
  fernauslese_channel: string;
  usage_type: PitchUsageType;
  restrict_arrival_types: boolean;
  arrival_type_settings: PitchArrivalTypeSettingsForm;
  feature_ids: string[];
}

function pitchToForm(
  pitch: Pitch,
  arrivalTypes: ArrivalTypeRecord[],
): PitchFormState {
  const restrict = hasArrivalTypeRestrictions(pitch.arrival_type_settings);
  return {
    code: pitch.code,
    label: pitch.label ?? "",
    category_id: pitch.category_id ?? "",
    size_sqm: pitch.size_sqm ?? "",
    has_electricity: pitch.has_electricity,
    has_water: pitch.has_water,
    has_sewage: pitch.has_sewage,
    is_blocked: pitch.is_blocked,
    is_active: pitch.is_active,
    notes: pitch.notes ?? "",
    fernauslese_channel:
      pitch.fernauslese_channel != null ? String(pitch.fernauslese_channel) : "",
    usage_type: pitch.usage_type ?? "tourism",
    restrict_arrival_types: restrict,
    arrival_type_settings: restrict
      ? arrivalSettingsFromApi(pitch.arrival_type_settings, arrivalTypes)
      : emptyArrivalTypeSettings(arrivalTypes),
    feature_ids: pitch.feature_ids ?? [],
  };
}

export function EditPitchModal({
  open,
  pitch,
  categories,
  onClose,
  onSaved,
  onDeleted,
  canEdit,
}: EditPitchModalProps) {
  const [form, setForm] = useState<PitchFormState | null>(null);
  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const { features } = usePitchFeatures(open);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDev = isDevDataDeletionHost();

  useEffect(() => {
    if (!open) return;
    api
      .get<ArrivalTypeRecord[]>("/api/v1/arrival-types")
      .then(setArrivalTypes)
      .catch(() => setArrivalTypes([]));
  }, [open]);

  useEffect(() => {
    if (!open || !pitch) return;
    setForm(pitchToForm(pitch, arrivalTypes));
    setError(null);
    setSubmitting(false);
  }, [open, pitch, arrivalTypes]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pitch || !form || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/pitches/${pitch.id}`, {
        code: form.code.trim(),
        label: form.label.trim() || null,
        category_id: form.category_id || null,
        size_sqm: form.size_sqm ? Number(form.size_sqm) : null,
        has_electricity: form.has_electricity,
        has_water: form.has_water,
        has_sewage: form.has_sewage,
        is_blocked: form.is_blocked,
        is_active: form.is_active,
        notes: form.notes.trim() || null,
        fernauslese_channel: form.fernauslese_channel.trim()
          ? Number(form.fernauslese_channel)
          : null,
        usage_type: form.usage_type,
        arrival_type_settings: arrivalSettingsToApi(
          form.arrival_type_settings,
          form.restrict_arrival_types,
        ),
        feature_ids: form.feature_ids,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSoftDelete() {
    if (!pitch || !canEdit) return;
    const confirmed = window.confirm(
      `Stellplatz ${pitch.code} ausblenden?\n\nDer Platz bleibt in der Datenbank erhalten, erscheint aber nicht mehr in der Platzliste und ist nicht mehr buchbar. Bestehende Buchungen bleiben unverändert.`,
    );
    if (!confirmed) return;
    setError(null);
    setDeleting(true);
    try {
      await api.post(`/api/v1/pitches/${pitch.id}/deactivate`);
      onDeleted?.();
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  }

  async function handleHardDelete() {
    if (!pitch || !canEdit || !isDev) return;
    const first = window.confirm(
      `Stellplatz ${pitch.code} ENDGÜLTIG aus der Datenbank löschen?\n\nNur in der Dev-Umgebung. Dies kann nicht rückgängig gemacht werden.`,
    );
    if (!first) return;
    const second = window.confirm(`Wirklich löschen? Code: ${pitch.code}`);
    if (!second) return;
    setError(null);
    setDeleting(true);
    try {
      await api.del(`/api/v1/dev/pitches/${pitch.id}`);
      onDeleted?.();
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Endgültiges Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  }

  if (!pitch || !form) return null;

  const readOnly = !canEdit;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Stellplatz ${pitch.code}`}
      description={canEdit ? "Platzdaten bearbeiten" : "Platzdetails (nur Lesen)"}
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Code *</span>
            <input
              className="eh-input"
              value={form.code}
              onChange={(e) => setForm((f) => f && { ...f, code: e.target.value })}
              required
              disabled={readOnly}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Bezeichnung</span>
            <input
              className="eh-input"
              value={form.label}
              onChange={(e) => setForm((f) => f && { ...f, label: e.target.value })}
              disabled={readOnly}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Platztyp</span>
            <select
              className="eh-select-input"
              value={form.usage_type}
              onChange={(e) =>
                setForm(
                  (f) => f && { ...f, usage_type: e.target.value as PitchUsageType },
                )
              }
              disabled={readOnly}
            >
              {PITCH_USAGE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {canEdit ? (
              <span className="mt-1 block text-xs text-label-tertiary">
                Saison- oder Halbsaison-Nutzung als Tourismusplatz: zuerst den Vertrag
                beenden, dann auf „Tourismusplatz“ umstellen. Buchungen und das Änderungsprotokoll
                bleiben am Platz erhalten; danach wieder auf „Dauerstellplatz“ zurückstellen.
              </span>
            ) : null}
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Kategorie</span>
            <select
              className="eh-select-input"
              value={form.category_id}
              onChange={(e) => setForm((f) => f && { ...f, category_id: e.target.value })}
              disabled={readOnly}
            >
              <option value="">Keine Kategorie</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Größe m²</span>
            <input
              className="eh-input"
              value={form.size_sqm}
              onChange={(e) => setForm((f) => f && { ...f, size_sqm: e.target.value })}
              disabled={readOnly}
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-label">
          {(
            [
              ["has_electricity", "Strom"],
              ["has_water", "Wasser"],
              ["has_sewage", "Abwasser"],
              ["is_blocked", "Dauerhaft gesperrt"],
              ["is_active", "Aktiv"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form[key]}
                onChange={(e) => setForm((f) => f && { ...f, [key]: e.target.checked })}
                disabled={readOnly}
              />
              {label}
            </label>
          ))}
        </div>

        <div className="space-y-1">
          <span className="block text-sm text-label-secondary">Eigenschaften</span>
          <PitchFeatureSelect
            features={features}
            selectedIds={form.feature_ids}
            onChange={(feature_ids) => setForm((f) => f && { ...f, feature_ids })}
            disabled={readOnly}
            emptyHint="Noch keine Eigenschaften angelegt – über „Eigenschaften verwalten“ in der Platzliste anlegen."
          />
        </div>

        {form.has_electricity ? (
          <label className="block max-w-xs text-sm">
            <span className="mb-1 block text-label-secondary">
              Fernauslese Kanal-ID
            </span>
            <input
              type="number"
              min={1}
              className="eh-input"
              value={form.fernauslese_channel}
              onChange={(e) =>
                setForm((f) => f && { ...f, fernauslese_channel: e.target.value })
              }
              placeholder="z. B. 1"
              disabled={readOnly}
            />
            <span className="mt-1 block text-xs text-label-tertiary">
              Channel-Nummer aus der Fernauslese-Zuordnungstabelle (nicht die Bezeichnung im
              Web-Frontend).
            </span>
          </label>
        ) : null}

        {arrivalTypes.length > 0 ? (
          <PitchArrivalTypeSettingsEditor
            arrivalTypes={arrivalTypes}
            restrictArrivalTypes={form.restrict_arrival_types}
            onRestrictChange={(restrict_arrival_types) =>
              setForm((f) =>
                f
                  ? {
                      ...f,
                      restrict_arrival_types,
                      arrival_type_settings: restrict_arrival_types
                        ? f.arrival_type_settings
                        : emptyArrivalTypeSettings(arrivalTypes),
                    }
                  : f,
              )
            }
            value={form.arrival_type_settings}
            onChange={(arrival_type_settings) =>
              setForm((f) => f && { ...f, arrival_type_settings })
            }
            disabled={readOnly}
          />
        ) : null}

        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Notizen</span>
          <textarea
            className="eh-input min-h-[4rem] resize-y"
            value={form.notes}
            onChange={(e) => setForm((f) => f && { ...f, notes: e.target.value })}
            rows={2}
            disabled={readOnly}
          />
        </label>

        <PitchBlockPeriodsEditor pitchId={pitch.id} canEdit={canEdit} />

        <AuditHistory entityType="pitch" entityId={pitch.id} active={open} />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap items-center justify-between gap-2 border-t border-separator pt-4">
          <div className="flex flex-wrap gap-2">
            {canEdit && pitch.is_active ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => void handleSoftDelete()}
                loading={deleting}
                disabled={submitting || deleting}
              >
                Platz löschen
              </Button>
            ) : null}
            {canEdit && isDev ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => void handleHardDelete()}
                loading={deleting}
                disabled={submitting || deleting}
              >
                Endgültig löschen (Dev)
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting || deleting}
            >
              {canEdit ? "Abbrechen" : "Schließen"}
            </Button>
            {canEdit ? (
              <Button type="submit" loading={submitting} disabled={deleting}>
                Speichern
              </Button>
            ) : null}
          </div>
        </div>
      </form>
    </Modal>
  );
}
